import React, { ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import { createUseStyles } from 'react-jss';
import cn from 'classnames';
import { Overlay } from '@tcl-argon-prefabs/overlay';
import { GrayscalePalette } from '@tcl-boron-colors/colors';

export const useElementWidth = () => {
  const [width, setWidth] = useState(0);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!ref.current) return;

    const observer = new ResizeObserver((entries) => {
      setWidth(entries[0].contentRect.width);
    });

    observer.observe(ref.current);

    return () => {
      observer.disconnect();
    };
  }, []);

  return { ref, width };
};

const useStyles = createUseStyles({
  root: {
    display: 'flex',
    flex: 1,
    height: '100%',
    minHeight: 0,
  },
  overlayRoot: {
    display: 'flex',
    flex: 1,
    flexDirection: 'column',
    height: '100%',
    minHeight: 0,
    overflow: 'hidden',
    width: '100%',
  },
  desktopRoot: {
    display: 'flex',
    flex: 1,
    flexDirection: 'column',
    height: '100%',
    minHeight: 0,
    overflow: 'hidden',
    width: '100%',
  },
  /** Overlay breakpoint: column stack under optional `contentTop`; primary scroll below overlays. */
  overlayMain: {
    display: 'flex',
    flex: 1,
    flexDirection: 'column',
    minHeight: 0,
    overflow: 'hidden',
  },
  content: {
    flex: 1,
    minHeight: 0,
    minWidth: 0,
    overflowY: 'auto',
  },
  contentScrollLocked: {
    overflow: 'hidden',
  },
  overlayPanel: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    minHeight: 0,
    overflow: 'hidden',
  },
  drawer: {
    bottom: 0,
    display: 'flex',
    flexDirection: 'column',
    minHeight: 0,
    overflow: 'hidden',
    position: 'absolute',
    top: 0,
    width: 400,
  },
  drawerX: {
    alignSelf: 'stretch',
    flexShrink: 0,
    height: '100%',
    minHeight: 0,
    overflow: 'hidden',
    position: 'relative',
    transition: 'width 0.3s ease',
    width: 400,
  },
  drawerY: {
    height: 200,
    overflow: 'hidden',
    position: 'relative',
    transition: 'height 0.3s ease',
  },
  hiddenX: {
    width: 0,
  },
  hiddenY: {
    height: 0,
  },
});

interface PageLayoutProps {
  content: ReactNode;
  contentLeft?: ReactNode;
  contentRight?: ReactNode;
  contentTop?: ReactNode;
  /** Desktop side-by-side layout: overrides default 400px width for the right column. */
  contentRightWidth?: number | string;
  onCloseLeft?: () => void;
  onCloseRight?: () => void;
  showContentLeft?: boolean;
  showContentRight?: boolean;
  showContentTop?: boolean;
}

export const PageLayout: React.FC<PageLayoutProps> = ({
  content,
  contentLeft,
  contentRight,
  contentTop,
  contentRightWidth,
  onCloseLeft,
  onCloseRight,
  showContentLeft = false,
  showContentRight = false,
  showContentTop = false,
}) => {
  const classes = useStyles();
  const { ref, width } = useElementWidth();

  // 800px threshold for switching to overlay mode
  // 400px content + 400px drawerX is the smallest reasonable width
  const isOverlay = useMemo(() => width < 800, [width]);
  const lockBackgroundScroll = isOverlay && (showContentLeft || showContentRight);

  if (isOverlay) {
    return (
      <div ref={ref} className={classes.overlayRoot}>
        {contentTop && (
          <div
            className={cn(classes.drawerY, { [classes.hiddenY]: !showContentTop })}
            style={{ borderBottom: `1px solid ${GrayscalePalette[300]}` }}
          >
            <div style={{ position: 'absolute', top: 0, width: '100%' }}>{contentTop}</div>
          </div>
        )}
        <div className={classes.overlayMain}>
          <Overlay isOpen={showContentLeft} openDirection="left" onRequestClose={onCloseLeft}>
            <div className={classes.overlayPanel}>{contentLeft}</div>
          </Overlay>
          <div
            className={cn(classes.content, lockBackgroundScroll && classes.contentScrollLocked)}
            data-page-layout-scroll-region
            data-testid="page-layout-scroll-region"
          >
            {content}
          </div>
          <Overlay isOpen={showContentRight} openDirection="right" onRequestClose={onCloseRight}>
            <div className={classes.overlayPanel}>{contentRight}</div>
          </Overlay>
        </div>
      </div>
    );
  }

  return (
    <div ref={ref} className={classes.desktopRoot}>
      {contentTop && (
        <div
          className={cn(classes.drawerY, { [classes.hiddenY]: !showContentTop })}
          style={{ borderBottom: `1px solid ${GrayscalePalette[300]}` }}
        >
          <div style={{ position: 'absolute', top: 0, width: '100%' }}>{contentTop}</div>
        </div>
      )}
      <div className={classes.root}>
        {contentLeft && (
          <div
            className={cn(classes.drawerX, { [classes.hiddenX]: !showContentLeft })}
            style={{ borderRight: `1px solid ${GrayscalePalette[300]}` }}
          >
            <div className={classes.drawer} style={{ right: 0 }}>
              {contentLeft}
            </div>
          </div>
        )}
        <div className={classes.content} data-page-layout-scroll-region data-testid="page-layout-scroll-region">
          {content}
        </div>
        {contentRight && (
          <div
            className={cn(classes.drawerX, { [classes.hiddenX]: !showContentRight })}
            style={{
              borderLeft: `1px solid ${GrayscalePalette[300]}`,
              ...(showContentRight && contentRightWidth != null ? { width: contentRightWidth } : {}),
            }}
          >
            <div
              className={classes.drawer}
              style={{
                left: 0,
                ...(showContentRight && contentRightWidth != null ? { width: contentRightWidth } : {}),
              }}
            >
              {contentRight}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
